import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton, SkeletonRow } from "@/components/Skeleton";
import { toast } from "sonner";
import {
  Users, BookOpen, Sparkles, Coins, TrendingUp, Award, Globe,
  Activity, Layers, Gauge, Wand2, Eye, Copy as CopyIcon,
  ShieldAlert, RefreshCw, ArrowUpRight, Crown, FileText, Compass,
} from "lucide-react";

/**
 * Premium SaaS-style admin growth analytics dashboard.
 * Wires `/api/admin/growth-stats` which requires institution-admin role.
 * Non-admins see a graceful "permission needed" empty state.
 */
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [reloading, setReloading] = useState(false);

  const load = async () => {
    setReloading(true);
    try {
      const { data: d } = await api.get("/admin/growth-stats");
      setData(d);
      setError(null);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 401) setError({ code: 401, msg: "Sign in to access analytics." });
      else if (code === 403) setError({ code: 403, msg: "Admin access required. Ask your TeeBee Institution admin to grant you access." });
      else setError({ code: code || 0, msg: "Could not load analytics. Try again in a moment." });
    } finally {
      setReloading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (error) {
    return (
      <Layout title="Growth Analytics">
        <div className="tb-card p-10 text-center max-w-xl mx-auto" data-testid="admin-locked">
          <span className="mx-auto h-14 w-14 grid place-items-center rounded-full bg-amber-100 dark:bg-amber-500/20 mb-3">
            <ShieldAlert className="h-6 w-6 text-amber-700 dark:text-amber-300" />
          </span>
          <p className="font-display text-lg font-medium">Admin lounge</p>
          <p className="mt-1 text-sm text-muted-foreground">{error.msg}</p>
          <div className="mt-5 flex flex-wrap gap-2 justify-center">
            <Button variant="outline" className="rounded-full" onClick={() => navigate("/institution")}>
              <Users className="h-3.5 w-3.5 mr-1.5" /> Institution page
            </Button>
            <Button className="rounded-full" onClick={() => navigate("/dashboard")}>Back to dashboard</Button>
          </div>
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout title="Growth Analytics">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64 mb-4" />
        <Skeleton className="h-64" />
      </Layout>
    );
  }

  const u = data.users || {};
  const c = data.content || {};
  const r = data.referrals || {};
  const coins = data.coins_by_kind || {};

  return (
    <Layout title="Growth Analytics">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
            <Crown className="h-3 w-3" /> Admin · TeeBee Institution
          </span>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
            Growth & impact analytics
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Live view of users, content, coin economy and referrals.
          </p>
        </div>
        <Button variant="outline" className="rounded-full" onClick={load} disabled={reloading} data-testid="admin-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${reloading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* KPI strip */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5 tb-stagger" data-testid="admin-kpis">
        <Kpi icon={Users}      chip="tb-chip-lavender" label="Total users"        value={u.total}        sub={`+${u.new_today || 0} today`} />
        <Kpi icon={Activity}   chip="tb-chip-mint"     label="Active this week"   value={u.active_week}  sub={`${u.new_week || 0} new in 7d`} />
        <Kpi icon={BookOpen}   chip="tb-chip-sky"      label="Lessons"            value={c.total_lessons} sub={`${c.public_lessons || 0} public`} />
        <Kpi icon={Sparkles}   chip="tb-chip-butter"   label="AI generations"     value={c.total_generations} sub={`${c.generations_week || 0} this week`} />
      </section>

      {/* Engagement + Coin economy strip */}
      <section className="grid grid-cols-12 gap-5 mb-5">
        {/* Engagement card */}
        <div className="col-span-12 lg:col-span-7 tb-card p-6" data-testid="admin-engagement">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground inline-flex items-center gap-2">
            <TrendingUp className="h-3 w-3" /> Engagement
          </p>
          <h3 className="mt-2 font-display text-lg font-medium">How teachers are using TeeBee</h3>
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Featured lessons" value={c.featured_lessons} icon={Award} />
            <Stat label="Lesson remixes"   value={c.total_remixes}    icon={Wand2} />
            <Stat label="Lesson copies"    value={c.total_duplicates} icon={CopyIcon} />
            <Stat label="Public views"     value={(data.top_lessons || []).reduce((a, l) => a + (l.view_count || 0), 0)} icon={Eye} />
          </div>
          <div className="mt-5">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Generation mix</p>
            <GenerationMixBars coinsByKind={coins} />
          </div>
        </div>

        {/* Coin economy card */}
        <div className="col-span-12 lg:col-span-5 tb-card p-6" data-testid="admin-coin-economy">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground inline-flex items-center gap-2">
            <Coins className="h-3 w-3" /> TeeBee Coin economy
          </p>
          <h3 className="mt-2 font-display text-lg font-medium">Earn · Spend · Burn</h3>
          <ul className="mt-5 space-y-2.5">
            <CoinRow label="Signup bonuses"  kind="signup_bonus" coins={coins} accent="tb-chip-mint" />
            <CoinRow label="Rewards"          kind="reward"       coins={coins} accent="tb-chip-butter" />
            <CoinRow label="Recharges"        kind="recharge"     coins={coins} accent="tb-chip-lavender" />
            <CoinRow label="Spent on AI"      kind="spend"        coins={coins} accent="tb-chip-sky" debit />
            <CoinRow label="Refunded"         kind="refund"       coins={coins} accent="tb-chip-mint" />
            <CoinRow label="Admin grants"     kind="admin_grant"  coins={coins} accent="tb-chip-rose" />
          </ul>
          <BurnRateBadge coins={coins} />
        </div>
      </section>

      {/* Referrals strip */}
      <section className="grid grid-cols-12 gap-5 mb-5">
        <div className="col-span-12 lg:col-span-5 tb-card p-6" data-testid="admin-referrals">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground inline-flex items-center gap-2">
            <Gauge className="h-3 w-3" /> Referrals
          </p>
          <h3 className="mt-2 font-display text-lg font-medium">Word of mouth</h3>
          <div className="mt-5 grid grid-cols-3 gap-3 text-center">
            <Stat label="Total"      value={r.total}     compact />
            <Stat label="Completed"  value={r.completed} compact />
            <Stat label="Conversion" value={`${Math.round(((r.conversion || 0) * 100))}%`} compact />
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7 tb-card p-6" data-testid="admin-top-creators">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground inline-flex items-center gap-2">
              <Award className="h-3 w-3" /> Top creators
            </p>
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">by public lessons</span>
          </div>
          {(!data.top_creators || data.top_creators.length === 0) ? (
            <p className="mt-4 text-sm text-muted-foreground italic">Top creators will appear once teachers publish lessons.</p>
          ) : (
            <ol className="mt-4 space-y-2">
              {data.top_creators.slice(0, 5).map((tc, i) => (
                <li key={tc._id || i} className="flex items-center gap-3">
                  <span className="h-7 w-7 rounded-full tb-chip-lavender grid place-items-center font-display text-xs font-medium">{i + 1}</span>
                  <p className="text-sm font-medium flex-1 truncate font-mono">{tc._id || "—"}</p>
                  <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><BookOpen className="h-3 w-3" /> {tc.published}</span>
                  <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {tc.views}</span>
                  <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><CopyIcon className="h-3 w-3" /> {tc.duplicates}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>

      {/* Top lessons */}
      <section className="tb-card p-6 mb-5" data-testid="admin-top-lessons">
        <div className="flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground inline-flex items-center gap-2">
            <FileText className="h-3 w-3" /> Top lessons
          </p>
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">by views</span>
        </div>
        {(!data.top_lessons || data.top_lessons.length === 0) ? (
          <p className="mt-4 text-sm text-muted-foreground italic">No public lessons yet.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  <th className="text-left py-2 pr-3">Title</th>
                  <th className="text-left py-2 pr-3 hidden md:table-cell">Author</th>
                  <th className="text-left py-2 pr-3">Subject</th>
                  <th className="text-right py-2 pr-3">Views</th>
                  <th className="text-right py-2">Copies</th>
                </tr>
              </thead>
              <tbody>
                {data.top_lessons.slice(0, 10).map((l, i) => (
                  <tr key={l.share_token || i} className="border-t border-border/60">
                    <td className="py-2 pr-3">
                      <span className="font-medium inline-flex items-center gap-2">
                        {l.is_featured && <Award className="h-3 w-3 text-amber-500" aria-label="Featured" />}
                        <span className="line-clamp-1">{l.title}</span>
                      </span>
                    </td>
                    <td className="py-2 pr-3 hidden md:table-cell text-muted-foreground text-xs">{l.publish_author_name || "—"}</td>
                    <td className="py-2 pr-3 text-xs">{l.subject || "—"}{l.grade ? ` · G${l.grade}` : ""}</td>
                    <td className="py-2 pr-3 text-right font-mono text-xs">{(l.view_count || 0).toLocaleString()}</td>
                    <td className="py-2 text-right font-mono text-xs">{(l.duplicate_count || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="text-[11px] text-muted-foreground text-right">
        Generated at {new Date(data.generated_at).toLocaleString()}
      </p>
    </Layout>
  );
}

/* ---------- leaf components ---------- */

function Kpi({ icon: Icon, chip, label, value, sub }) {
  return (
    <div className="tb-card p-5">
      <div className="flex items-center justify-between">
        <span className={`h-9 w-9 rounded-xl grid place-items-center ${chip}`}>
          <Icon className="h-4 w-4" />
        </span>
        {sub && <span className="text-[10px] uppercase tracking-[0.18em] font-medium text-muted-foreground">{sub}</span>}
      </div>
      <p className="mt-3 font-display text-3xl tracking-tight font-medium">{(value || 0).toLocaleString()}</p>
      <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

function Stat({ label, value, icon: Icon, compact = false }) {
  return (
    <div>
      {Icon && !compact && (
        <span className="h-8 w-8 rounded-xl tb-chip-mint grid place-items-center mb-2">
          <Icon className="h-4 w-4" />
        </span>
      )}
      <p className={`${compact ? "font-display text-2xl" : "font-display text-xl"} tracking-tight font-medium`}>
        {typeof value === "number" ? (value || 0).toLocaleString() : (value || "—")}
      </p>
      <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
    </div>
  );
}

function CoinRow({ label, kind, coins, accent, debit = false }) {
  const k = coins[kind] || { amount: 0, count: 0 };
  const amount = Math.abs(k.amount || 0);
  return (
    <li className="flex items-center gap-3">
      <span className={`h-7 w-7 rounded-lg ${accent} grid place-items-center`}>
        <Coins className="h-3.5 w-3.5" />
      </span>
      <span className="flex-1 text-sm">{label}</span>
      <span className="text-[11px] text-muted-foreground font-mono">{(k.count || 0).toLocaleString()} txns</span>
      <span className={`font-mono text-sm font-medium ${debit ? "text-rose-500" : "text-emerald-600 dark:text-emerald-400"}`}>
        {debit ? "−" : "+"}{amount.toLocaleString()}
      </span>
    </li>
  );
}

function GenerationMixBars({ coinsByKind }) {
  // Bar chart of coin spend by feature kind (top 6 by absolute spend)
  const entries = Object.entries(coinsByKind || {})
    .filter(([k]) => k === "spend")
    .flatMap(() => []); // we don't have feature-level breakdown in this endpoint
  // Fallback: render a simple capacity bar from total spend vs grant
  const grant = Math.abs((coinsByKind.signup_bonus?.amount || 0) + (coinsByKind.reward?.amount || 0) + (coinsByKind.recharge?.amount || 0));
  const spend = Math.abs(coinsByKind.spend?.amount || 0);
  const pct = grant > 0 ? Math.min(100, Math.round((spend / grant) * 100)) : 0;
  if (entries.length === 0) {
    return (
      <div className="mt-3">
        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
          <span>Burn rate</span>
          <span className="font-mono">{pct}%</span>
        </div>
        <div className="mt-1 h-2 rounded-full bg-muted overflow-hidden">
          <div
            className={`h-full ${pct > 80 ? "bg-rose-500" : pct > 50 ? "bg-amber-500" : "bg-primary"} transition-all duration-700`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">{spend.toLocaleString()} coins spent against {grant.toLocaleString()} granted/recharged.</p>
      </div>
    );
  }
  return null;
}

function BurnRateBadge({ coins }) {
  const grant = Math.abs((coins.signup_bonus?.amount || 0) + (coins.reward?.amount || 0) + (coins.recharge?.amount || 0));
  const spend = Math.abs(coins.spend?.amount || 0);
  if (!grant && !spend) return null;
  const net = grant - spend;
  return (
    <div className="mt-5 rounded-2xl bg-muted/40 p-3 border border-border/60 flex items-center gap-3">
      <span className="h-9 w-9 rounded-xl tb-chip-sky grid place-items-center">
        <ArrowUpRight className="h-4 w-4" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Net coin float</p>
        <p className={`font-display text-lg tracking-tight font-medium ${net >= 0 ? "" : "text-rose-500"}`}>
          {net >= 0 ? "+" : "−"}{Math.abs(net).toLocaleString()}
        </p>
      </div>
    </div>
  );
}
